/* zx-product.js — behaviour for sections/fairytales-product-custom.liquid
 * <zx-product> custom element: media gallery, variant picker, quantity, live viewers, sticky bar.
 * No layout reads inside scroll handlers except one cached width; everything is event driven. */
if (!customElements.get('zx-product')) {
  customElements.define(
    'zx-product',
    class ZxProduct extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.cleanup = [];
        this.strings = this.readJson('[data-zx-strings]') || { add: 'Add to cart', soldOut: 'Sold out', unavailable: 'Unavailable' };
        this.initGallery();
        this.initVariants();
        this.initQuantity();
        this.initViewers();
        this.initSticky();
      }

      disconnectedCallback() {
        this.cleanup.forEach((fn) => fn());
        this.cleanup = [];
        this.ready = false;
      }

      readJson(selector) {
        const el = this.querySelector(selector);
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          return null;
        }
      }

      /* ---------------- gallery ---------------- */
      initGallery() {
        const track = this.querySelector('[data-zx-track]');
        if (!track) return;
        this.track = track;
        this.slides = Array.from(track.querySelectorAll('[data-zx-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-zx-thumb]'));
        this.dots = Array.from(this.querySelectorAll('.ft-media-dot'));
        this.prevBtn = this.querySelector('[data-zx-prev]');
        this.nextBtn = this.querySelector('[data-zx-next]');
        this.index = 0;
        if (this.slides.length < 2) return;

        /* slides 2+ are <template>s until needed: on first interaction, or once the page is idle after load */
        const hydrateAll = () => this.slides.forEach((s) => this.hydrate(s));
        ['pointerdown', 'touchstart', 'focusin'].forEach((type) =>
          this.querySelector('.ft-product-gallery').addEventListener(type, hydrateAll, { once: true, passive: true })
        );
        const idle = () => ('requestIdleCallback' in window ? window.requestIdleCallback(hydrateAll, { timeout: 3000 }) : setTimeout(hydrateAll, 1500));
        if (document.readyState === 'complete') idle();
        else window.addEventListener('load', idle, { once: true });

        let width = track.clientWidth;
        if ('ResizeObserver' in window) {
          const ro = new ResizeObserver((entries) => {
            width = entries[0].contentRect.width;
          });
          ro.observe(track);
          this.cleanup.push(() => ro.disconnect());
        }

        let queued = false;
        track.addEventListener(
          'scroll',
          () => {
            if (queued) return;
            queued = true;
            requestAnimationFrame(() => {
              queued = false;
              if (width) this.setActive(Math.round(track.scrollLeft / width));
            });
          },
          { passive: true }
        );

        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.zxThumb)))
        );
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.index - 1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.index + 1));
        track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
        });
        this.setActive(0);
      }

      hydrate(slide) {
        const tpl = slide && slide.querySelector('template[data-zx-defer]');
        if (tpl) tpl.replaceWith(tpl.content.cloneNode(true));
      }

      goTo(i) {
        if (!this.track || !this.slides.length) return;
        const target = Math.max(0, Math.min(this.slides.length - 1, i));
        this.hydrate(this.slides[target]);
        this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
        this.setActive(target);
      }

      setActive(i) {
        const index = Math.max(0, Math.min(this.slides.length - 1, i));
        if (index === this.index && this.galleryPainted) return;
        this.galleryPainted = true;
        const previous = this.slides[this.index];
        this.index = index;
        this.thumbs.forEach((t, n) => {
          const on = n === index;
          t.classList.toggle('is-active', on);
          if (on) {
            t.setAttribute('aria-current', 'true');
            const list = t.parentElement;
            const left = t.offsetLeft - list.offsetLeft;
            const top = t.offsetTop - list.offsetTop;
            if (list.scrollWidth > list.clientWidth) list.scrollTo({ left: left - list.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' });
            else if (list.scrollHeight > list.clientHeight) list.scrollTo({ top: top - list.clientHeight / 2 + t.clientHeight / 2, behavior: 'smooth' });
          } else {
            t.removeAttribute('aria-current');
          }
        });
        this.dots.forEach((d, n) => d.classList.toggle('is-active', n === index));
        if (this.prevBtn) this.prevBtn.disabled = index === 0;
        if (this.nextBtn) this.nextBtn.disabled = index === this.slides.length - 1;
        /* stop a playing video when its slide leaves */
        if (previous && previous !== this.slides[index]) {
          const v = previous.querySelector('video');
          if (v && !v.paused) v.pause();
        }
      }

      showMedia(mediaId) {
        if (!mediaId || !this.slides) return;
        const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (i >= 0) this.goTo(i);
      }

      /* ---------------- variants ---------------- */
      initVariants() {
        this.variants = this.readJson('[data-zx-variants]');
        this.fieldsets = Array.from(this.querySelectorAll('[data-zx-option]'));
        if (!this.variants || !this.fieldsets.length) return;
        this.addEventListener('change', (e) => {
          if (e.target.matches('[data-zx-option] input[type="radio"]')) this.onOptionChange();
        });
        this.refreshAvailability(this.selectedOptions());
      }

      selectedOptions() {
        return this.fieldsets.map((fs) => {
          const checked = fs.querySelector('input:checked');
          return checked ? checked.value : null;
        });
      }

      onOptionChange() {
        const options = this.selectedOptions();
        this.fieldsets.forEach((fs, i) => {
          const label = fs.querySelector('[data-zx-option-value]');
          if (label) label.textContent = options[i] || '';
        });
        const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
        this.refreshAvailability(options);
        this.applyVariant(variant);
      }

      /* mark values that cannot be bought together with the other current choices */
      refreshAvailability(options) {
        this.fieldsets.forEach((fs, optIndex) => {
          fs.querySelectorAll('input[type="radio"]').forEach((input) => {
            const ok = this.variants.some(
              (v) => v.available && v.options[optIndex] === input.value &&
                v.options.every((o, i) => i === optIndex || options[i] === null || o === options[i])
            );
            const label = input.nextElementSibling;
            if (label) label.classList.toggle('is-unavailable', !ok);
          });
        });
      }

      applyVariant(variant) {
        const ids = this.querySelectorAll('[data-zx-variant-id]');
        const buttons = this.querySelectorAll('[data-zx-add]');
        const now = this.querySelector('[data-zx-price-now]');
        const was = this.querySelector('[data-zx-price-was]');
        const sticky = this.querySelector('[data-zx-sticky-price]');

        if (!variant) {
          buttons.forEach((b) => { b.disabled = true; b.textContent = this.strings.unavailable; });
          this.querySelectorAll('[data-zx-buy]').forEach((b) => { b.disabled = true; });
          return;
        }
        ids.forEach((input) => { input.value = variant.id; });
        buttons.forEach((b) => {
          b.disabled = !variant.available;
          b.textContent = variant.available ? this.strings.add : this.strings.soldOut;
        });
        this.querySelectorAll('[data-zx-buy]').forEach((b) => { b.disabled = !variant.available; });
        if (now) now.textContent = variant.price;
        if (sticky) sticky.textContent = variant.price;
        if (was) {
          was.hidden = !variant.compare;
          was.textContent = variant.compare || '';
        }
        if (variant.media) this.showMedia(variant.media);
        if (this.dataset.url && window.history && history.replaceState) {
          try {
            history.replaceState(history.state, '', `${this.dataset.url}?variant=${variant.id}`);
          } catch (e) {
            /* sandboxed previews can refuse URL updates; the selection itself still works */
          }
        }
        this.dispatchEvent(new CustomEvent('zx:variant:change', { bubbles: true, detail: { variant } }));
      }

      /* ---------------- quantity ---------------- */
      initQuantity() {
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-zx-qty]');
          if (!btn) return;
          const input = btn.parentElement.querySelector('input[name="quantity"]');
          if (!input) return;
          const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.zxQty));
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      /* ---------------- live viewers ---------------- */
      initViewers() {
        const box = this.querySelector('[data-zx-viewers]');
        const out = box && box.querySelector('[data-zx-viewers-count]');
        if (!out) return;
        const min = Number(box.dataset.min) || 1;
        const max = Math.max(min, Number(box.dataset.max) || min);
        let value = Math.round(min + Math.random() * (max - min));
        out.textContent = value;
        let visible = true;
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; });
          io.observe(box);
          this.cleanup.push(() => io.disconnect());
        }
        const timer = setInterval(() => {
          if (!visible || document.hidden) return;
          value = Math.max(min, Math.min(max, value + Math.round((Math.random() - 0.5) * 4)));
          out.textContent = value;
        }, 4500);
        this.cleanup.push(() => clearInterval(timer));
      }

      /* ---------------- sticky add to cart ---------------- */
      initSticky() {
        const bar = this.querySelector('[data-zx-sticky]');
        const main = this.querySelector('.ft-product-form [data-zx-add]');
        if (!bar || !main || !('IntersectionObserver' in window)) return;
        const io = new IntersectionObserver((entries) => {
          const e = entries[0];
          /* show only after the main button has scrolled above the viewport */
          bar.hidden = e.isIntersecting || e.boundingClientRect.top > 0;
        });
        io.observe(main);
        this.cleanup.push(() => io.disconnect());
      }
    }
  );
}
